/**
 * Amplify configuration, and the question of whether auth is configured at all.
 *
 * The three values come from the build. A build that lacks them is not a build that
 * should quietly fall back to "signed in" — it is a misconfigured deploy, and the
 * sign-in page says so plainly instead of showing a password box that cannot work.
 *
 * `VITE_DEV_USER` is the local escape hatch: no Cognito running on a laptop, so the
 * API accepts an `x-dev-user` header instead. The backend refuses that header unless
 * ALLOW_DEV_AUTH is set, and it never is in a deployed environment, so a build that
 * carried this by accident would fail closed rather than open.
 */

import { Amplify } from 'aws-amplify';

const userPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID;
const userPoolClientId = import.meta.env.VITE_COGNITO_CLIENT_ID;

export const DEV_USER = import.meta.env.VITE_DEV_USER as string | undefined;

/** True when real Cognito sign-in is available in this build. */
export const authConfigured = Boolean(userPoolId && userPoolClientId);

if (authConfigured) {
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: userPoolId as string,
        userPoolClientId: userPoolClientId as string,
        signUpVerificationMethod: 'code',
        loginWith: { email: true },
      },
    },
  });
}

/**
 * How long a session may last.
 *
 * Two independent limits, both required by the client:
 *  - idle: no interaction for this long and the session ends, because an office
 *    browser left open is the realistic risk, not a stolen token.
 *  - absolute: a ceiling regardless of activity, so a machine someone keeps nudging
 *    cannot stay signed in indefinitely.
 *
 * Enforced in the browser, which is a usability control rather than a security one —
 * the real limits are Cognito's token lifetimes and revocation. Both are honest about
 * that: signing out revokes the refresh token server-side.
 */
export const IDLE_LIMIT_MS = 120 * 60 * 1000;
export const ABSOLUTE_LIMIT_MS = 5 * 60 * 60 * 1000;

/** How long the "you are about to be signed out" warning shows before it happens. */
export const IDLE_WARNING_MS = 60 * 1000;

/** Where the absolute deadline is kept, so a reload cannot reset it. */
export const SESSION_START_KEY = 'hh.session.start';
export const SESSION_END_REASON_KEY = 'hh.session.endReason';
